import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Rating,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFunctions, httpsCallable } from "firebase/functions";

import { app } from "../firebase";
import usePlaygrounds from "../hooks/usePlaygrounds";
import useGeolocation from "../hooks/useGeolocation";
import { sortByDistance } from "../utils/geo";
import PlaygroundMap from "../components/playgrounds/PlaygroundMap";
import PlaygroundList from "../components/playgrounds/PlaygroundList";

const functions = getFunctions(app);
const fetchPlaygroundsFn = httpsCallable(functions, "fetchPlaygrounds");

const PlaygroundPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { playgrounds } = usePlaygrounds();
  const { position, error: geoError, loading: geoLoading } = useGeolocation();

  const [minRating, setMinRating] = useState(0);

  const filtered = useMemo(
    () =>
      minRating > 0
        ? playgrounds.filter((p) => (p.rating || 0) >= minRating)
        : playgrounds,
    [playgrounds, minRating],
  );

  const sorted = useMemo(
    () => sortByDistance(filtered, position),
    [filtered, position],
  );

  const [selectedId, setSelectedId] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null); // { ok, count } | { ok: false }

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const res = await fetchPlaygroundsFn();
      setRefreshResult({ ok: true, count: res.data.count });
    } catch (err) {
      console.error("Failed to refresh playgrounds:", err);
      setRefreshResult({ ok: false });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.page", pt: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
          <Tooltip title={t("nav.back")}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" fontWeight={600}>
            {t("playgrounds.title")}
          </Typography>
        </Box>

        {geoLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {geoError === "denied" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("playgrounds.location_denied")}
          </Alert>
        )}
        {geoError === "unsupported" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("playgrounds.location_unsupported")}
          </Alert>
        )}

        <Paper
          elevation={10}
          sx={{
            borderRadius: 2,
            p: 2,
            mb: 2,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("playgrounds.min_rating_label")}
          </Typography>
          <Rating
            value={minRating}
            onChange={(_, value) => setMinRating(value || 0)}
          />
          {minRating > 0 && (
            <Button size="small" onClick={() => setMinRating(0)}>
              {t("playgrounds.clear_filter")}
            </Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
            {t("playgrounds.result_count", { count: sorted.length })}
          </Typography>
        </Paper>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper
            elevation={10}
            sx={{ borderRadius: 2, overflow: "hidden", flex: { md: 2 }, minWidth: 0 }}
          >
            <PlaygroundMap
              playgrounds={sorted}
              userPosition={position}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </Paper>

          <Paper
            elevation={10}
            sx={{ borderRadius: 2, overflow: "hidden", flex: { md: 1 }, minWidth: 0 }}
          >
            <PlaygroundList
              playgrounds={sorted}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </Paper>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={
              refreshing ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {t("playgrounds.refresh_button")}
          </Button>
        </Box>

        {refreshResult?.ok && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t("playgrounds.refresh_success", { count: refreshResult.count })}
          </Alert>
        )}
        {refreshResult?.ok === false && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("playgrounds.refresh_error")}
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default PlaygroundPage;
